/* @flow */
import { ActionTypes } from 'groups/constants/GroupConstants';
import Group from 'entities/Group';
import Groups from 'collections/Groups';
import { PushEvents } from 'pusher/PusherConstants';
import Message from 'entities/Message';
import GroupActions from 'groups/actions/GroupActions';
import { rootGroup } from 'entities/RootGroup';
import Store from 'lib/Store';

/**
 * All the current user's groups.
 *
 * NOTE that groups that are loaded from the server will not necessarily be
 * stored here until they become one of the user's groups.
 */
class GroupStore extends Store {
  getInitialState() {
    return {
      archivedLoaded: false,
      loaded: false,
      groupCodeToId: {},
      groupNameToId: {},
      groups: new Groups(),
      showDraft: false,
    };
  }

  setGroupCreatedDuringSetup(group: Group): void {
    this.state.groupCreatedDuringSetup = group;
  }

  getGroupCreatedDuringSetup() {
    return this.state.groupCreatedDuringSetup;
  }

  clearGroupCreatedDuringSetup(): void {
    delete this.state.groupCreatedDuringSetup;
  }

  // TODO: Improve. This is a cheap implementation to get around some loading issues.
  // See https://github.com/remind101/r101-frontend/issues/339
  isLoaded(): boolean {
    return this.state.loaded;
  }

  isArchivedLoaded(): boolean {
    return this.state.archivedLoaded;
  }

  invalidate(): void {
    this.state.loaded = false;
  }

  isConnectedToGroup(group: Group): boolean {
    return (this.getGroup(group.id) !== undefined);
  }

  /**
   * Filters out archived groups.
   */
  getGroups(): Groups {
    const unarchivedGroups = this.state.groups.filter(group => !group.isArchived());
    return new Groups(unarchivedGroups);
  }

  selectGroups(predicate: Function): Groups {
    const selectedItems = this.state.groups.filter(predicate);
    return new Groups(selectedItems);
  }

  getOwnedGroups(): Groups {
    return this.selectGroups(group => group.getType() === 'owned' && !group.isArchived());
  }

  getInvitableClasses(): Groups {
    return this.selectGroups(group => ['owned', 'subscribed'].includes(group.getType()) && group.canAddPeople());
  }

  getAdministratedGroups(): Groups {
    return this.selectGroups(group =>
      group.isStaffClass() &&
      group.getPostableRoles().includes('admin')
    );
  }

  getAdministratedGroupByOrganizationId(organizationID: number) {
    return this.getAdministratedGroups().find(group => {
      return group.getStaffClassOrgId() === organizationID;
    });
  }

  getInvitableGroups(): Groups {
    return this.selectGroups(group => group.canAddPeople());
  }

  hasOwnedGroups(): boolean {
    return this.getOwnedGroups().length > 0;
  }

  getSubscribedGroups(): Groups {
    return this.selectGroups(group => group.getType() === 'subscribed');
  }

  getSchoolGroups(): Groups {
    return this.selectGroups(group => group.isStaffClass());
  }

  getDistrictGroups(): Groups {
    return this.selectGroups(group => group.getType() === 'district');
  }

  getGroupsAtOrganization(orgId: number): Groups {
    return this.selectGroups(g => g.getOrganization() && g.getOrganization().id === orgId && !g.isStaffClass());
  }

  /**
   * @deprecated use postable roles instead
   */
  getPostableGroups(): Groups {
    return this.selectGroups(group => group.canPost());
  }

  getGroup(groupId: number): ?Group {
    return this.state.groups.get(groupId);
  }

  getGroupForCode(groupCode: string): ?Group {
    if (groupCode === rootGroup.getCode()) {
      return rootGroup;
    }

    const id = this.state.groupCodeToId[groupCode];
    if (id) {
      return this.getGroup(id);
    }

    return null;
  }

  getOwnedGroupForName(groupName): ?Group {
    return this.getOwnedGroups().find(g => g.getName() === groupName);
  }

  getStaffClassForOrg(orgId: number): ?Group {
    return this.state.groups.find(g => g.getStaffClassOrgId() === orgId);
  }

  getArchivedGroups(): Group[] {
    return this.state.groups.filter(group => group.isArchived());
  }

  /* This is called when group is added/updated or deleted
   * because is the group code or group name is updated
   * then we need to flush the old value.
   */
  resetMappings(): void {
    this.state.groupCodeToId = [];
    this.state.groupNameToId = [];
    this.state.archivedGroupCodeToId = [];

    this.state.groups.forEach(group => {
      this.state.groupCodeToId[group.getCode()] = group.id;
      this.state.groupNameToId[group.getName().toLowerCase()] = group.id;
    });
  }

  validateLastReadSeq(oldGroup: Group, newGroup: Group): boolean {
    return (
      !oldGroup.getLastReadSeq() ||
      !newGroup.getLastReadSeq() ||
      newGroup.getLastReadSeq() >= oldGroup.getLastReadSeq()
    );
  }

  addGroup(group: Group): void {
    const oldGroup = this.state.groups.get(group);
    if (!oldGroup || this.validateLastReadSeq(oldGroup, group)) {
      this.state.groups.add(group);
    }
  }

  deleteGroup(group): void {
    this.state.groups.remove(group);
  }

  // for AnnouncementComposer, within the universal composer dialog
  setDraftAnnouncement(message: Message): void {
    this.state.currentComposerMessage = message;
  }

  getShowDraft(): boolean {
    return this.state.showDraft;
  }

  setShowDraft(val: boolean): void {
    this.state.showDraft = val;
  }

  _clearDraftAnnouncement(): void {
    this.state.currentComposerMessage = null;
  }

  getDraftAnnouncement(): ?Message {
    return this.state.currentComposerMessage;
  }

  handleDispatch(action: Object): void {
    switch (action.type) {
      case ActionTypes.RESET_STORE:
        this.resetState();
        break;
      case ActionTypes.UPDATE_GROUP:
      case ActionTypes.LOAD_GROUP:
        this.handleLoadGroups([action.group]);
        break;
      case ActionTypes.RESIGN_OWNERSHIP:
        this.handleDeleteGroups([action.group]);
        break;
      case ActionTypes.LIST_GROUPS:
        if (action.allMine) {
          this.handleSetGroups(action.groups);
        } else {
          this.handleLoadGroups(action.groups);
        }
        break;
      case ActionTypes.CREATE_GROUP:
      case ActionTypes.GROUPS_UPDATED:
        this.handleUpdateGroups(action.groups);
        break;
      case ActionTypes.LIST_ARCHIVED_GROUPS:
        this.handleLoadGroups(action.groups);
        this.state.archivedLoaded = true;
        break;
      case ActionTypes.ARCHIVE:
        this.handleUpdateGroups([action.group]);
        break;
      case ActionTypes.ARCHIVE_AND_REUSE:
        this.handleUpdateGroups([action.group, action.archivedGroup]);
        break;
      case ActionTypes.REUSE:
        this.handleUpdateGroups([action.group, action.archivedGroup]);
        break;
      case ActionTypes.RESTORE:
        this.handleUpdateGroups([action.group]);
        break;
      case PushEvents.CHANGESET_DESTROY_GROUP: {
        const uuids = action.changes.map(change => change.uuid);
        this.state.groups = new Groups(this.state.groups.filter(group => {
          return !uuids.includes(group.getUuid());
        }));

        this.resetMappings();
        this.emitChange();
        break;
      }
      case PushEvents.CHANGESET_CREATE_GROUP:
      case PushEvents.CHANGESET_UPDATE_GROUP: {
        const uuids = action.changes.map(change => change.uuid);
        GroupActions.loadGroups(uuids);
        break;
      }
      case ActionTypes.SEND_AGAIN:
        this.setDraftAnnouncement(action.message);
        this.setShowDraft(true);
        this.emitChange();
        break;
      case ActionTypes.SAVE_AND_SHOW_DRAFT_ANNOUNCEMENT:
        this.setDraftAnnouncement(action.message);
        this.setShowDraft(true);
        this.emitChange();
        break;
      case ActionTypes.FINISH_SHOWING_DRAFT:
        this.setShowDraft(false);
        this.emitChange();
        break;
      case ActionTypes.SAVE_DRAFT_ANNOUNCEMENT:
        this.setDraftAnnouncement(action.message);
        this.emitChange();
        break;
      case ActionTypes.CLEAR_DRAFT_ANNOUNCEMENT:
        this._clearDraftAnnouncement();
        this.emitChange();
        break;
      case ActionTypes.SUBSCRIBED:
        this.handleLoadGroups([action.group]);
        break;
      case ActionTypes.UNSUBSCRIBED:
        this.handleDeleteGroups([action.group]);
        break;
      case ActionTypes.SEND_GROUP_MESSAGE:
        this.handleMessageSent(action.message);
        break;
      default:
        break;
    }
  }

  handleSetGroups(groups: Group[]): void {
    const ids = groups.map(group => group.id);
    const toRemove = this.state.groups.filter(group => !ids.includes(group.id));

    groups.forEach(group => this.addGroup(group));
    toRemove.forEach(group => this.deleteGroup(group));

    this.resetMappings();
    this.state.loaded = true;
    this.emitChange();
  }

  handleLoadGroups(groups): void {
    groups.forEach(group => this.addGroup(group));
    this.resetMappings();
    this.emitChange();
  }

  handleDeleteGroups(groups): void {
    groups.forEach(group => this.deleteGroup(group));
    this.resetMappings();
    this.emitChange();
  }

  handleUpdateGroups(groups): void {
    groups.forEach(group => this.addGroup(group));
    this.resetMappings();
    this.emitChange();
  }

  handleMessageSent(message: Message): void {
    const threads = message.getThreads();
    threads.forEach(t => {
      const group = this.getGroup(t.group_id);
      if (group) {
        const groupThreads = group.getThreads();
        groupThreads.forEach(gt => {
          if (gt.role === t.role) {
            group.setThreadPreview(gt.role, message.getPreview(), message.getSentAt());
          }
        });
      }
    });
    this.emitChange();
  }
}

export default new GroupStore();



// WEBPACK FOOTER //
// ./src/groups/stores/GroupStore.js